import { FC, useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { cleanDataNewQuestionnaire } from 'ducks/modules'
import axios from 'lib/axios'
import { specialAdminRoutes, superAdminRoutes } from 'router/routes'
import { useNotification } from 'lib/context/notification.context'
import BluesiteTableComponent from 'views/components/UI/BluesiteTable'
import { Button } from 'views/components/UI/Buttons'
import { MainLayout } from 'views/layouts'
import { headers } from './constants'
import {
  Container,
  ContainerActions,
  TextViewMore,
} from './style'

const MIN_LIMIT = 10

type QuestionnaireRow = {
  id: number
  name: string
  description: string
  status: string
  total_questions: number
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  published: 'Publicado',
  archived: 'Archivado',
}

type ActionComponentProps = {
  id: number
  name: string
}

const ActionComponent: FC<ActionComponentProps> = ({ id, name }) => {
  const questionnaireState = {
    questionnaireId: id,
    questionnaireName: name,
  }

  return (
    <ContainerActions>
      <Link to={superAdminRoutes.SettingQuestionaire} state={questionnaireState}>
        <TextViewMore>Ver más</TextViewMore>
      </Link>
    </ContainerActions>
  )
}

const Questionary: FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { getError, getWarning } = useNotification()
  const role = useSelector((state: any) => state.auth.role)
  const hasQuestionnaireManagement = useSelector((state: any) => state.user.hasQuestionnaireManagement)
  const canManageQuestionnaires =
    role !== 'platform_admin' &&
    (role === 'super_admin' || hasQuestionnaireManagement)
  const [questionnaires, setQuestionnaires] = useState<QuestionnaireRow[]>([])
  const [searchParam, setSearchParam] = useState('')
  const [totalPages, setTotalPages] = useState(1)
  const [currentPage, setCurrentPage] = useState(1)

  const getQuestionnaires = useCallback(
    async (page = 1, limit = MIN_LIMIT, searchValue = '') => {
      try {
        const response = await axios.get('/company-questionnaire', {
          params: {
            page,
            limit,
            search: searchValue || undefined,
          },
        })

        const questionnairesData = response?.data?.questionnaires || []

        setQuestionnaires(
          questionnairesData.map((questionnaire: any) => ({
            id: questionnaire.id,
            name: questionnaire.name,
            description: questionnaire.description || '',
            status:
              STATUS_LABELS[questionnaire.status] ||
              questionnaire.status ||
              STATUS_LABELS.draft,
            total_questions: questionnaire.questions?.length || 0,
          }))
        )
        setTotalPages(response?.data?.total_pages || 1)
        setCurrentPage(page)
      } catch (error: any) {
        setQuestionnaires([])
        setTotalPages(1)
        const message =
          error?.response?.data?.message ||
          'No fue posible cargar los cuestionarios'
        getError(message)
      }
    },
    [getError]
  )

  const fetchSearchData = useCallback(
    (value: string, page = 1, limit = MIN_LIMIT) => {
      const normalizedValue = value.trim()
      setSearchParam(normalizedValue)
      getQuestionnaires(page, limit, normalizedValue)
    },
    [getQuestionnaires]
  )

  useEffect(() => {
    dispatch(cleanDataNewQuestionnaire())
  }, [dispatch])

  useEffect(() => {
    const shouldBlockAccess =
      role === 'platform_admin' ||
      (role === 'super_platform_admin' && !canManageQuestionnaires)

    if (shouldBlockAccess) {
      getWarning('Comunicate con el administrador.')
      navigate('/', { replace: true })
      return
    }

    getQuestionnaires()
  }, [canManageQuestionnaires, getQuestionnaires, getWarning, navigate, role])

  const handleNewQuestionnaire = () => {
    if (!canManageQuestionnaires) {
      getWarning('Comunicate con el administrador.')
      return
    }

    navigate(specialAdminRoutes.NewQuestionnaireBetesa)
  }

  return (
    <MainLayout>
      <Container>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: 35,
            marginTop: 50,
            alignItems: 'baseline',
          }}
        >
          <h2 style={{ fontSize: 32 }}>Cuestionarios</h2>
          <Button type="button" onClick={handleNewQuestionnaire}>
            Nuevo cuestionario
          </Button>
        </div>

        <BluesiteTableComponent
          ActionComponent={ActionComponent}
          data={questionnaires}
          headers={headers}
          searchBar={true}
          totalPages={totalPages}
          currentPage={currentPage}
          handlePagination={(page) => getQuestionnaires(page, MIN_LIMIT, searchParam)}
          onSearch={fetchSearchData}
          setSearchParam={setSearchParam}
          handleSearchFunction={() => fetchSearchData(searchParam)}
          searchLabel="Buscar cuestionario por:"
        />
      </Container>
    </MainLayout>
  )
}

export default Questionary
